import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { fetchLatestInsightsSnapshot } from '../../services/insightsService';

const PageContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  position: relative;
  display: grid;
  gap: var(--space-sm);
  margin-bottom: var(--space-lg);
  padding: var(--space-lg);
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  background:
    radial-gradient(circle at top right, rgba(130, 172, 216, 0.28), transparent 34%),
    linear-gradient(135deg, #fff 0%, var(--color-paper) 58%, rgba(236, 239, 235, 0.88) 100%);
  box-shadow: var(--shadow-small);

  &::after {
    content: '';
    position: absolute;
    right: -90px;
    bottom: -130px;
    width: 300px;
    height: 300px;
    border: 1px solid rgba(93, 64, 34, 0.14);
    border-radius: 50%;
  }
`;

const Eyebrow = styled.div`
  color: var(--color-accent);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;

const HeaderRow = styled.div`
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--space-md);

  @media (max-width: 720px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const TitleGroup = styled.div`
  display: grid;
  gap: var(--space-xs);
`;

const Title = styled.h1`
  margin: 0;
  color: var(--color-primary);
  font-family: var(--font-serif);
  font-size: clamp(2.5rem, 6vw, 5rem);
  font-weight: var(--font-weight-semibold);
  letter-spacing: -0.04em;
`;

const Subtitle = styled.p`
  max-width: 720px;
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-md);
`;

const TimestampBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 42px;
  padding: var(--space-xs) var(--space-sm);
  border: 1px solid var(--color-border);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  white-space: nowrap;
`;

const FinanceGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-md);
  margin-bottom: var(--space-lg);

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const MetricCard = styled.article`
  position: relative;
  display: grid;
  gap: var(--space-sm);
  min-height: 190px;
  padding: var(--space-lg);
  overflow: hidden;
  border: 1px solid ${({ $featured }) => $featured ? 'rgba(33, 26, 18, 0.18)' : 'var(--color-border)'};
  border-radius: var(--border-radius-md);
  background: ${({ $featured }) => $featured
    ? 'linear-gradient(145deg, var(--color-ink), var(--color-espresso))'
    : 'var(--color-surface)'};
  color: ${({ $featured }) => $featured ? 'white' : 'var(--color-text)'};
  box-shadow: ${({ $featured }) => $featured ? 'var(--shadow-large)' : 'var(--shadow-small)'};

  &::before {
    content: '';
    position: absolute;
    inset: auto -80px -120px auto;
    width: 240px;
    height: 240px;
    border-radius: 50%;
    background: ${({ $featured }) => $featured ? 'rgba(130, 172, 216, 0.22)' : 'rgba(130, 172, 216, 0.12)'};
  }
`;

const MetricLabel = styled.div`
  position: relative;
  color: ${({ $featured }) => $featured ? 'rgba(255, 255, 255, 0.72)' : 'var(--color-text-secondary)'};
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

const MetricValue = styled.div`
  position: relative;
  color: inherit;
  font-family: var(--font-serif);
  font-size: clamp(2.25rem, 5vw, 4.25rem);
  font-weight: var(--font-weight-semibold);
  line-height: 0.95;
  letter-spacing: -0.05em;
  font-variant-numeric: tabular-nums;
`;

const MetricHelper = styled.p`
  position: relative;
  max-width: 340px;
  margin: 0;
  color: ${({ $featured }) => $featured ? 'rgba(255, 255, 255, 0.72)' : 'var(--color-text-secondary)'};
  font-size: var(--font-size-sm);
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-md);

  @media (max-width: 1120px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.section`
  padding: var(--space-lg);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  background: rgba(255, 255, 255, 0.82);
  box-shadow: var(--shadow-small);
`;

const PanelHeader = styled.div`
  margin-bottom: var(--space-md);
  padding-bottom: var(--space-sm);
  border-bottom: 1px solid var(--color-border-light);
`;

const PanelTitle = styled.h2`
  margin: 0;
  color: var(--color-primary);
  font-family: var(--font-serif);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
`;

const PanelDescription = styled.p`
  margin: 4px 0 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
`;

const MetricList = styled.div`
  display: grid;
  gap: var(--space-sm);
`;

const CompactMetric = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: var(--space-md);
  align-items: center;
  padding: var(--space-sm);
  border: 1px solid var(--color-border-light);
  border-radius: var(--border-radius-md);
  background: var(--color-surface);
`;

const LinkedCompactMetric = styled(Link)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: var(--space-md);
  align-items: center;
  padding: var(--space-sm);
  border: 1px solid var(--color-border-light);
  border-radius: var(--border-radius-md);
  background: var(--color-surface);
  color: inherit;
  text-decoration: none;

  &:hover {
    border-color: var(--color-accent);
    background: rgba(130, 172, 216, 0.08);
  }
`;

const CompactLabel = styled.div`
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
`;

const CompactValue = styled.div`
  color: var(--color-primary);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  font-variant-numeric: tabular-nums;
`;

const StateCard = styled.div`
  display: grid;
  justify-items: center;
  gap: var(--space-sm);
  min-height: 360px;
  padding: var(--space-xl);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  text-align: center;
`;

const StateTitle = styled.h2`
  margin: 0;
  color: var(--color-primary);
  font-family: var(--font-serif);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
`;

const ErrorCard = styled(StateCard)`
  border-color: rgba(180, 35, 24, 0.28);
  background: rgba(180, 35, 24, 0.08);
  color: var(--color-error);
`;

const RefreshButton = styled.button`
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  background: white;
  color: var(--color-text-secondary);
  padding: var(--space-xs) var(--space-sm);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);

  &:hover:not(:disabled) {
    border-color: var(--color-accent);
    color: var(--color-primary);
  }
`;

const OperationalNote = styled.p`
  margin: var(--space-md) 0 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
`;

const numberFormatter = new Intl.NumberFormat('en-GB');

const formatNumber = (value) => numberFormatter.format(Number(value || 0));

const formatCurrency = (pence) => new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  maximumFractionDigits: 0,
}).format(Number(pence || 0) / 100);

const formatTimestamp = (generatedAtMs) => {
  if (!generatedAtMs) {
    return 'Snapshot not generated yet';
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(generatedAtMs));
};

const Insights = () => {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadSnapshot = async () => {
    setLoading(true);
    setError('');

    try {
      setSnapshot(await fetchLatestInsightsSnapshot());
    } catch (err) {
      console.error('Failed to load admin insights snapshot:', err);
      setError('Unable to load the latest insights snapshot.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;

    const run = async () => {
      setLoading(true);
      setError('');

      try {
        const data = await fetchLatestInsightsSnapshot();
        if (active) {
          setSnapshot(data);
        }
      } catch (err) {
        console.error('Failed to load admin insights snapshot:', err);
        if (active) {
          setError('Unable to load the latest insights snapshot.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <PageContainer>
        <StateCard>
          <StateTitle>Loading insights...</StateTitle>
          <p>Reading the latest admin snapshot from Firestore.</p>
        </StateCard>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorCard role="alert">
          <StateTitle>Insights unavailable</StateTitle>
          <p>{error}</p>
          <RefreshButton type="button" onClick={loadSnapshot}>Retry</RefreshButton>
        </ErrorCard>
      </PageContainer>
    );
  }

  if (!snapshot) {
    return (
      <PageContainer>
        <StateCard>
          <StateTitle>No insights snapshot yet</StateTitle>
          <p>The twice-daily snapshot job will populate this page after its next run.</p>
          <RefreshButton type="button" onClick={loadSnapshot}>Refresh</RefreshButton>
        </StateCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <Eyebrow>Site intelligence</Eyebrow>
        <HeaderRow>
          <TitleGroup>
            <Title>Insights</Title>
            <Subtitle>
              Latest operating snapshot for finance, artist/collector activity, and artwork availability.
            </Subtitle>
          </TitleGroup>
          <TimestampBadge>
            Updated {formatTimestamp(snapshot.generatedAtMs)}
          </TimestampBadge>
        </HeaderRow>
      </PageHeader>

      <FinanceGrid aria-label="Finance insights">
        <MetricCard $featured>
          <MetricLabel $featured>Total available value</MetricLabel>
          <MetricValue>{formatCurrency(snapshot.finance.availableArtworkValuePence)}</MetricValue>
          <MetricHelper $featured>
            Published GBP artworks owned by active artists and available for purchase.
          </MetricHelper>
        </MetricCard>
        <MetricCard>
          <MetricLabel>Subscribed artists</MetricLabel>
          <MetricValue>{formatNumber(snapshot.finance.subscribedArtists)}</MetricValue>
          <MetricHelper>
            Active or trialing artist subscriptions, excluding test artists.
          </MetricHelper>
        </MetricCard>
        <MetricCard>
          <MetricLabel>Total transacted volume</MetricLabel>
          <MetricValue>{formatCurrency(snapshot.finance.totalTransactedVolumePence)}</MetricValue>
          <MetricHelper>
            Collector totals from platform orders that have not been refunded.
          </MetricHelper>
        </MetricCard>
      </FinanceGrid>

      <SectionGrid>
        <Panel>
          <PanelHeader>
            <PanelTitle>Artist numbers</PanelTitle>
            <PanelDescription>Account activation and application pipeline.</PanelDescription>
          </PanelHeader>
          <MetricList>
            <CompactMetric>
              <CompactLabel>Active artists</CompactLabel>
              <CompactValue>{formatNumber(snapshot.artists.activeArtists)}</CompactValue>
            </CompactMetric>
            <CompactMetric>
              <CompactLabel>Inactive artists</CompactLabel>
              <CompactValue>{formatNumber(snapshot.artists.inactiveArtists)}</CompactValue>
            </CompactMetric>
            <CompactMetric>
              <CompactLabel>Total artist profiles</CompactLabel>
              <CompactValue>{formatNumber(snapshot.artists.totalArtists)}</CompactValue>
            </CompactMetric>
            <LinkedCompactMetric to="/artists/requests" aria-label="View open pending artist requests">
              <CompactLabel>Open pending requests</CompactLabel>
              <CompactValue>{formatNumber(snapshot.artists.pendingRegistrationRequests)}</CompactValue>
            </LinkedCompactMetric>
          </MetricList>
        </Panel>

        <Panel>
          <PanelHeader>
            <PanelTitle>Collector numbers</PanelTitle>
            <PanelDescription>Signup volume and completed identity verification.</PanelDescription>
          </PanelHeader>
          <MetricList>
            <CompactMetric>
              <CompactLabel>Total collector signups</CompactLabel>
              <CompactValue>{formatNumber(snapshot.collectors.totalSignups)}</CompactValue>
            </CompactMetric>
            <CompactMetric>
              <CompactLabel>Completed identity verification</CompactLabel>
              <CompactValue>{formatNumber(snapshot.collectors.identityVerified)}</CompactValue>
            </CompactMetric>
          </MetricList>
        </Panel>

        <Panel>
          <PanelHeader>
            <PanelTitle>Artwork numbers</PanelTitle>
            <PanelDescription>Marketplace inventory by operational status.</PanelDescription>
          </PanelHeader>
          <MetricList>
            <CompactMetric>
              <CompactLabel>Publicly available artworks</CompactLabel>
              <CompactValue>{formatNumber(snapshot.artworks.publiclyAvailable)}</CompactValue>
            </CompactMetric>
            <CompactMetric>
              <CompactLabel>Sold artworks</CompactLabel>
              <CompactValue>{formatNumber(snapshot.artworks.sold)}</CompactValue>
            </CompactMetric>
            <CompactMetric>
              <CompactLabel>Draft artworks</CompactLabel>
              <CompactValue>{formatNumber(snapshot.artworks.draft)}</CompactValue>
            </CompactMetric>
          </MetricList>
        </Panel>
      </SectionGrid>

      <OperationalNote>
        Data is updated hourly.
      </OperationalNote>
    </PageContainer>
  );
};

export default Insights;
